import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell, CheckCheck } from 'lucide-react';
import type { NotificationDTO } from '@btw/shared';
import { useMarkNotificationsRead, useNotifications } from '../api/hooks';
import { useDismiss } from '../lib/hooks';
import { useI18n } from '../i18n';
import { CountBadge } from '../components/Badges';
import { EntityAvatar } from '../components/Entity';
import { Skeleton } from '../components/Feedback';

/**
 * Bell + panel. The badge counts only unread "needs my action" items; the panel has two columns:
 * "Vereist actie" and "Ter info" (§4.2, §9.5). Notifications never show amounts (FR-23).
 */
export function NotificationBell() {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const q = useNotifications();
  const markRead = useMarkNotificationsRead();
  useDismiss(ref, open, () => setOpen(false));
  const unread = q.data?.unreadActionCount ?? 0;
  const items = q.data?.items ?? [];
  const action = items.filter((n) => n.actionRequired);
  const info = items.filter((n) => !n.actionRequired);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        className="icon-btn"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={unread ? t('notif.ariaCount', { n: unread }) : t('notif.title')}
      >
        <Bell size={20} strokeWidth={1.5} aria-hidden="true" />
        <CountBadge n={unread} />
      </button>
      {open && (
        <div className="popover" role="dialog" aria-label={t('notif.title')} style={{ right: 0, top: 'calc(100% + 6px)', width: 'min(420px, calc(100vw - 24px))', padding: 0, position: 'absolute' }}>
          <div className="row row--between" style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-default)' }}>
            <h2 style={{ fontSize: 'var(--fs-h3)' }}>{t('notif.title')}</h2>
            <button type="button" className="btn btn--text btn--sm" onClick={() => markRead.mutate('all')} disabled={!items.some((n) => !n.readAt)}>
              <CheckCheck size={16} aria-hidden="true" /> {t('notif.markAll')}
            </button>
          </div>
          <div style={{ maxHeight: '65vh', overflow: 'auto', padding: '4px 8px 12px' }}>
            {q.isLoading && (
              <div className="stack-sm" style={{ padding: 12 }}>
                <Skeleton h={16} w="70%" />
                <Skeleton h={16} w="50%" />
              </div>
            )}
            <NotifGroup title={t('notif.action')} items={action} empty={t('notif.actionEmpty')} onOpen={(n) => { if (!n.readAt) markRead.mutate([n.id]); setOpen(false); }} />
            <NotifGroup title={t('notif.info')} items={info} empty={t('notif.infoEmpty')} onOpen={(n) => { if (!n.readAt) markRead.mutate([n.id]); setOpen(false); }} />
          </div>
        </div>
      )}
    </div>
  );
}

function NotifGroup({ title, items, empty, onOpen }: { title: string; items: NotificationDTO[]; empty: string; onOpen: (n: NotificationDTO) => void }) {
  return (
    <section>
      <div className="menu-label" style={{ marginTop: 8 }}>
        {title} {items.length > 0 && `(${items.length})`}
      </div>
      {items.length === 0 ? <p className="muted small" style={{ padding: '4px 10px 8px' }}>{empty}</p> : items.map((n) => <NotifItem key={n.id} n={n} onOpen={() => onOpen(n)} />)}
    </section>
  );
}

function notificationHref(n: NotificationDTO): string {
  const e = n.entity?.id;
  const q = n.quarter;
  switch (n.kind) {
    case 'questions_waiting':
      return '/vragen';
    case 'missing_documents':
      return '/documenten?view=purchase';
    case 'payment_failed':
    case 'plan_suspended':
      return '/instellingen/abonnement';
    case 'channel_check':
    case 'company_check':
      return e ? `/instellingen/bedrijven/${e}` : '/instellingen/bedrijven';
    case 'invite':
      return '/instellingen/leden';
    case 'kor_threshold':
      return e ? `/kor/${e}` : '/';
    default:
      return e && q ? `/aangiftes/${e}/${q}` : '/';
  }
}

function NotifItem({ n, onOpen }: { n: NotificationDTO; onOpen: () => void }) {
  const { t, fmt } = useI18n();
  const params = { ...n.params, entity: n.entity?.name ?? '', quarter: n.quarter ? fmt.quarter(n.quarter) : '' };
  return (
    <Link to={notificationHref(n)} className="menu-item" onClick={onOpen} style={{ alignItems: 'flex-start', padding: '10px', minHeight: 56 }}>
      {n.entity ? <EntityAvatar entity={n.entity} size="sm" /> : <Bell size={18} aria-hidden="true" />}
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontWeight: n.readAt ? 400 : 600 }}>{t(`notif.kind.${n.kind}`, params)}</span>
        <span className="muted small">
          {n.entity ? `${n.entity.name} · ` : ''}
          {fmt.relativeDay(n.createdAt)}
        </span>
      </span>
      {!n.readAt && <span className="visually-hidden">{t('notif.unread')}</span>}
      {!n.readAt && <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: 4, background: 'var(--brand)', marginTop: 8, flex: 'none' }} />}
    </Link>
  );
}
